<template>
  <q-page class="cookies-page">
    <KdsNav
      :logo-label="t('app.name')"
      :links="navLinks"
      :cta-secondary="t('landing.nav.logIn')"
      @cta-secondary-click="goAuth"
    />

    <div class="cookies-page__inner">
      <!-- Hero -->
      <header class="cookies-page__hero">
        <KdsEyebrow>{{ t('cookies.eyebrow') }}</KdsEyebrow>
        <h1 class="cookies-page__title">{{ t('cookies.title') }}</h1>
      </header>

      <LegalDocsLayout :anchors="cookiesAnchors">
        <!-- Section 1 — What cookies are for -->
        <section id="section-1" class="cookies-page__section" data-reveal>
          <h2 class="cookies-page__section-title">{{ t('cookies.section1.title') }}</h2>
          <p
            v-for="(paragraph, i) in tm('cookies.section1.paragraphs')"
            :key="i"
            class="cookies-page__section-body"
          >{{ rt(paragraph) }}</p>
        </section>

        <!-- Section 2 — Cookies we use -->
        <section id="section-2" class="cookies-page__section cookies-page__section--bordered" data-reveal>
          <h2 class="cookies-page__section-title">{{ t('cookies.section2.title') }}</h2>
          <p class="cookies-page__section-body">{{ t('cookies.section2.intro') }}</p>
          <div class="cookies-page__table-wrapper">
            <table class="cookies-page__table">
              <thead>
                <tr>
                  <th
                    v-for="(header, i) in tm('cookies.section2.tableHeaders')"
                    :key="i"
                    class="cookies-page__table-th"
                  >{{ rt(header) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in tm('cookies.section2.tableRows')"
                  :key="i"
                  class="cookies-page__table-row"
                >
                  <td class="cookies-page__table-td cookies-page__table-td--mono">{{ rt((row as CookieRow).name) }}</td>
                  <td class="cookies-page__table-td">{{ rt((row as CookieRow).type) }}</td>
                  <td class="cookies-page__table-td">{{ rt((row as CookieRow).purpose) }}</td>
                  <td class="cookies-page__table-td">{{ rt((row as CookieRow).duration) }}</td>
                  <td class="cookies-page__table-td">{{ rt((row as CookieRow).issuer) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="cookies-page__section-body">{{ t('cookies.section2.outro') }}</p>
        </section>

        <!-- Section 3 — Tools we do not use -->
        <section id="section-3" class="cookies-page__section cookies-page__section--bordered" data-reveal>
          <h2 class="cookies-page__section-title">{{ t('cookies.section3.title') }}</h2>
          <p class="cookies-page__section-body">{{ t('cookies.section3.intro') }}</p>
          <ul class="cookies-page__list">
            <li
              v-for="(item, i) in tm('cookies.section3.items')"
              :key="i"
              class="cookies-page__list-item"
            >{{ rt(item) }}</li>
          </ul>
          <p class="cookies-page__section-body">{{ t('cookies.section3.outro') }}</p>
        </section>

        <!-- Section 4 — Manage preferences -->
        <section id="section-4" class="cookies-page__section cookies-page__section--bordered" data-reveal>
          <h2 class="cookies-page__section-title">{{ t('cookies.section4.title') }}</h2>
          <p class="cookies-page__section-body">{{ t('cookies.section4.intro') }}</p>
          <ul class="cookies-page__list">
            <li
              v-for="(item, i) in tm('cookies.section4.items')"
              :key="i"
              class="cookies-page__list-item"
            >{{ rt(item) }}</li>
          </ul>
          <p class="cookies-page__section-body">{{ t('cookies.section4.outro') }}</p>
        </section>

        <!-- Section 5 — Updates -->
        <section id="section-5" class="cookies-page__section cookies-page__section--bordered" data-reveal>
          <h2 class="cookies-page__section-title">{{ t('cookies.section5.title') }}</h2>
          <p
            v-for="(paragraph, i) in tm('cookies.section5.paragraphs')"
            :key="i"
            class="cookies-page__section-body"
          >{{ rt(paragraph) }}</p>
        </section>
      </LegalDocsLayout>
    </div>

    <KdsFooter
      :brand="t('app.name')"
      :status-label="t('landing.footer.status')"
      :status-to="'/status'"
      :columns="footerColumns"
    />
  </q-page>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import KdsNav from '@/kds/components/KdsNav.vue';
import KdsFooter from '@/kds/components/KdsFooter.vue';
import KdsEyebrow from '@/kds/primitives/KdsEyebrow.vue';
import LegalDocsLayout from '@/components/static/LegalDocsLayout.vue';
import { useRevealOnScroll } from '@/composables/useRevealOnScroll';
import { useConsent } from '@/composables/useConsent';
import { useSeoMeta } from '@/composables/useSeoMeta';

type CookieRow = {
  name: string;
  type: string;
  purpose: string;
  duration: string;
  issuer: string;
};

const { t, tm, rt } = useI18n();
useSeoMeta('cookies');
useRevealOnScroll();
const router = useRouter();
const { openPreferences } = useConsent();

const navLinks = computed(() => [
  { label: t('landing.nav.product'), href: '/#how' },
  { label: t('landing.nav.protocols'), href: '/#protocols' },
  { label: t('landing.nav.pricing'), href: '/#pricing' },
  { label: t('landing.nav.docs'), href: '/docs' },
  { label: t('landing.nav.changelog'), href: '/changelogs' }
]);

const cookiesAnchors = computed(() => [
  { label: t('cookies.section1.title'), anchor: 'section-1' },
  { label: t('cookies.section2.title'), anchor: 'section-2' },
  { label: t('cookies.section3.title'), anchor: 'section-3' },
  { label: t('cookies.section4.title'), anchor: 'section-4' },
  { label: t('cookies.section5.title'), anchor: 'section-5' }
]);

const footerColumns = computed(() => [
  {
    title: t('landing.footer.productTitle'),
    links: [
      { label: t('landing.footer.linkOverview'), href: '/#how' },
      { label: t('landing.footer.linkProtocols'), href: '/#protocols' },
      { label: t('landing.footer.linkPricing'), href: '/#pricing' }
    ]
  },
  {
    title: t('landing.footer.resourcesTitle'),
    links: [
      { label: t('landing.footer.linkDocs'), to: '/docs' },
      { label: t('landing.footer.linkChangelog'), to: '/changelogs' },
      { label: t('landing.footer.linkStatus'), to: '/status' }
    ]
  },
  {
    title: t('landing.footer.companyTitle'),
    links: [
      { label: t('landing.footer.linkAbout'), to: '/about' },
      { label: t('landing.footer.linkContact'), to: { path: '/about', hash: '#contact' } }
    ]
  },
  {
    title: t('landing.footer.legalTitle'),
    links: [
      { label: t('landing.footer.linkPrivacy'), to: '/privacy' },
      { label: t('landing.footer.linkTerms'), to: '/terms' },
      { label: t('landing.footer.linkCookies'), to: '/cookies' },
      { label: t('landing.footer.linkLegalNotice'), to: '/legal-notice' },
      { label: t('landing.footer.manageCookies'), action: openPreferences }
    ]
  }
]);

const goAuth = (): void => {
  void router.push({ name: 'auth' });
};
</script>

<style scoped lang="scss">
.cookies-page {
  background: var(--paper);
  color: var(--ink);

  &__inner {
    max-width: 1280px;
    margin: 0 auto;
    padding: var(--space-11) var(--space-7);
  }

  &__hero {
    margin-bottom: var(--space-9);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  &__title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h1);
    line-height: 1.18;
    letter-spacing: -0.015em;
    color: var(--ink);
  }

  &__section {
    padding-top: var(--space-9);
    scroll-margin-top: calc(var(--nav-height, 56px) + var(--space-2));
    display: flex;
    flex-direction: column;
    gap: var(--space-5);

    &--bordered {
      margin-top: var(--space-9);
      border-top: var(--border);
    }
  }

  &__section-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h2);
    line-height: 1.22;
    letter-spacing: -0.012em;
    color: var(--ink);
  }

  &__section-body {
    margin: 0;
    font-size: var(--fs-body);
    line-height: 1.65;
    color: var(--stone);

    a {
      color: var(--ink);
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  &__list {
    margin: 0;
    padding-left: var(--space-6);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  &__list-item {
    font-size: var(--fs-body);
    line-height: 1.65;
    color: var(--stone);
  }

  &__table-wrapper {
    overflow-x: auto;
    border: var(--border);
    border-radius: var(--r-4);
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--fs-body-sm);
    color: var(--stone);
  }

  &__table-th {
    padding: var(--space-3) var(--space-4);
    text-align: left;
    font-weight: var(--fw-medium);
    color: var(--ink);
    background: var(--paper-2);
    border-bottom: var(--border);
    white-space: nowrap;
    font-family: var(--font-ui);
    font-size: var(--fs-meta);
    letter-spacing: 0.01em;
  }

  &__table-row {
    border-bottom: var(--border);

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: var(--paper-2);
    }
  }

  &__table-td {
    padding: var(--space-3) var(--space-4);
    vertical-align: top;
    line-height: 1.5;

    &--mono {
      font-family: var(--font-mono);
      font-size: var(--fs-mono);
      color: var(--ink);
      white-space: nowrap;
    }
  }
}
</style>
